// =============================================================================
// domain/multiSource.ts — two loggers writing to one account.
// -----------------------------------------------------------------------------
// 🚨 THIS IS THE REPLACEMENT FOR A GUARD THAT WAS MEASURED AND ABANDONED.
//
// HIGH 3 of `docs/INGEST-REVIEW-2026-08-09.md` is that `trade-ingest` keys
// idempotency on the LOGGER'S OWN order id, so a Sierra study and an ATAS logger
// pushing the same execution never collide. The obvious fix — refuse a push
// whose CONTENT already exists on that account from a different source — was
// designed, then measured against the beta user's real book before being built:
//
//   592 realtime pushes · 18 days with BOTH loggers running
//   content-fingerprint pairs across the two sources: **ZERO**
//
// Not "few". None. The two loggers never describe a trade the same way: ATAS
// aggregates a position into one row where Sierra splits it into fills, so the
// quantities, exit times and prices all differ. **There is no row-to-row
// correspondence to find**, and a row-level guard would have fired zero times
// while implying the problem was handled.
//
// ⚠ The clincher is one account-day in his book where Sierra and ATAS agree to
// the dollar — 2 rows, 4 lots, −$889 from each — and STILL share no content
// fingerprint. When even a perfect double-count cannot be matched row for row,
// the row is the wrong unit.
//
// ── SO THIS DETECTS RATHER THAN DEDUPES ──
//
// The unit is the ACCOUNT-DAY. If two sources wrote trades to one account on one
// day, that account's numbers for that day are wrong, and the only honest thing
// the product can do is SAY SO. It cannot know which source is right — that is a
// question about the trader's setup, not about the data.
//
// 🔴 THE HARM THIS ADDRESSES IS NOT HYPOTHETICAL. The beta user ran Sierra and
// ATAS into one broker account for roughly six weeks. **The product never
// mentioned it.** It was found by a human investigation, and by then his book
// carried duplicate pairs that are still there.
// =============================================================================

/** The fields the detector reads. Deliberately not a whole Trade. */
export interface SourcedTrade {
  /** The account's own id. */
  accountId: string | null;
  /** The platform account string, which is what the trader recognises. */
  sourceAccount?: string | null;
  date: string | null;
  qty: number | null;
  netPnl: number | null;
  /**
   * Which logger pushed this — `extras.ingest.source`.
   *
   * ⚠ NULL for anything that did not arrive by realtime push: a file import, a
   * hand-entered trade. Those are EXCLUDED, because "imported a file and also
   * ran a logger" is a different situation with a different answer (undo the
   * import), and lumping them together would fire on a book that is fine.
   */
  ingestSource: string | null;
}

export interface SourceTally {
  source: string;
  rows: number;
  lots: number;
  netPnl: number;
}

export interface MultiSourceDay {
  /** What the trader sees on screen — the platform account string if there is one. */
  account: string;
  date: string;
  /** Descending by row count, so the largest contributor reads first. */
  sources: SourceTally[];
  /**
   * Rows and contracts beyond the LARGEST source.
   *
   * ⚠ AN ESTIMATE OF THE OVERCOUNT, NOT A FACT, and the copy must say so. It
   * assumes the biggest source is the complete one, which is a guess: on
   * 2026-08-05 the smaller source held the day's largest loss. It is offered to
   * size the problem, never to decide what to delete.
   */
  extraRows: number;
  extraLots: number;
}

const key = (t: SourcedTrade): string =>
  `${t.sourceAccount || t.accountId || ''}\u0000${t.date || ''}`;

/**
 * Account-days where more than one logger wrote.
 *
 * Sorted newest first — the most recent conflict is the one the trader can still
 * do something about.
 */
export function detectMultiSourceDays(
  trades: readonly SourcedTrade[],
): MultiSourceDay[] {
  const cells = new Map<string, Map<string, SourceTally>>();

  for (const t of trades) {
    // ⚠ Realtime pushes only — see `ingestSource`.
    if (!t.ingestSource || !t.date) continue;
    const k = key(t);
    let bySource = cells.get(k);
    if (!bySource) { bySource = new Map(); cells.set(k, bySource); }
    let tally = bySource.get(t.ingestSource);
    if (!tally) {
      tally = { source: t.ingestSource, rows: 0, lots: 0, netPnl: 0 };
      bySource.set(t.ingestSource, tally);
    }
    tally.rows += 1;
    tally.lots += Number(t.qty) || 0;
    tally.netPnl += Number(t.netPnl) || 0;
  }

  const out: MultiSourceDay[] = [];
  for (const [k, bySource] of cells) {
    if (bySource.size < 2) continue;
    const [account, date] = k.split('\u0000') as [string, string];
    const sources = [...bySource.values()].sort((a, b) => b.rows - a.rows);
    let extraRows = 0, extraLots = 0;
    for (let i = 1; i < sources.length; i++) {
      extraRows += sources[i]!.rows;
      extraLots += sources[i]!.lots;
    }
    out.push({ account, date, sources, extraRows, extraLots });
  }

  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export interface MultiSourceSummary {
  /** Account-days affected. */
  days: number;
  /** Distinct accounts affected — what the warning counts. */
  accounts: string[];
  /** Every logger seen across the conflicts, for naming them in the copy. */
  sources: string[];
  extraRows: number;
  extraLots: number;
  /** The most recent conflicted date, or null. */
  latest: string | null;
}

/**
 * One line's worth of facts about the whole book.
 *
 * ⚠ Counts ACCOUNTS, not rows, because that is the unit of the fix: the trader
 * turns a logger off for an account. "21 duplicate trades" invites hunting for
 * 21 rows, which is exactly the row-level thinking that does not work here.
 */
export function summariseMultiSource(days: readonly MultiSourceDay[]): MultiSourceSummary {
  const accounts = new Set<string>();
  const sources = new Set<string>();
  let extraRows = 0, extraLots = 0, latest: string | null = null;

  for (const d of days) {
    accounts.add(d.account);
    for (const s of d.sources) sources.add(s.source);
    extraRows += d.extraRows;
    extraLots += d.extraLots;
    if (!latest || d.date > latest) latest = d.date;
  }

  return {
    days: days.length,
    accounts: [...accounts].sort(),
    sources: [...sources].sort(),
    extraRows,
    extraLots,
    latest,
  };
}
